import { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import cloneService from '../../services/cloneService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';

const PAGE_SIZE = 20;

const CloneProductsIndex = () => {
  const { isRtl } = useLocalization();
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [cloningId, setCloningId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await cloneService.getSourceProducts({ search, page, size: PAGE_SIZE });
      setItems(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error('Error loading source products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleClone = async (product) => {
    if (product.existsInTarget || cloningId) return;
    setCloningId(product.id);
    try {
      await cloneService.cloneProduct(product.id);
      setItems((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, existsInTarget: true } : p))
      );
    } catch (error) {
      const status = error.response?.status;
      if (status === 409) {
        setItems((prev) =>
          prev.map((p) => (p.id === product.id ? { ...p, existsInTarget: true } : p))
        );
        alert(isRtl ? 'هذا المنتج موجود بالفعل' : 'This product already exists');
      } else {
        console.error('Error cloning product:', error);
        alert(isRtl ? 'تعذر استنساخ المنتج' : 'Failed to clone product');
      }
    } finally {
      setCloningId(null);
    }
  };

  return (
    <div className="clone-products-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'استنساخ من المتجر الرئيسي' : 'Clone from Main Store'}</h1>
            <p className="page-description">
              {isRtl
                ? 'تصفح منتجات المتجر الرئيسي (rimalalkhaleej.com) وانسخها إلى هذا المتجر.'
                : "Browse products from the main store (rimalalkhaleej.com) and copy them into this store."}
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <form className="search-box" onSubmit={submitSearch}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث عن منتج...' : 'Search products...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </form>
          <div className="card-header-info hidden-mobile">
            <span className="text-muted">
              {isRtl ? `${totalCount} منتج` : `${totalCount} products`}
            </span>
          </div>
        </div>

        <div className="card-body p-0">
          {isLoading ? (
            <Loading text={isRtl ? 'جاري التحميل...' : 'Loading products...'} />
          ) : items.length === 0 ? (
            <div className="empty-state">
              <p className="empty-description">{isRtl ? 'لا توجد منتجات' : 'No products found'}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>{isRtl ? 'المنتج' : 'Product'}</th>
                    <th>{isRtl ? 'الفئة' : 'Category'}</th>
                    <th>{isRtl ? 'العلامة التجارية' : 'Brand'}</th>
                    <th>{isRtl ? 'الحالة' : 'Status'}</th>
                    <th>{isRtl ? 'الإجراءات' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((product) => {
                    const imageUrl = getProductImageUrl(product.primaryImageUrl);
                    return (
                      <tr key={product.id}>
                        <td>
                          <div className="product-cell">
                            <div className="product-thumb-wrap">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={product.nameEn}
                                  className="product-thumb"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : null}
                            </div>
                            <div className="product-info">
                              <div className="product-name">{isRtl ? product.nameAr : product.nameEn}</div>
                              {isRtl && product.nameEn ? <div className="product-name-sub">{product.nameEn}</div> : null}
                            </div>
                          </div>
                        </td>
                        <td><span className="text-secondary">{product.categoryNameEn || '-'}</span></td>
                        <td><span className="text-secondary">{isRtl ? product.brandNameAr : product.brandNameEn}</span></td>
                        <td>
                          <span className={`badge ${product.existsInTarget ? 'badge-success' : 'badge-warning'}`}>
                            {product.existsInTarget
                              ? (isRtl ? 'موجود' : 'Exists')
                              : (isRtl ? 'غير موجود' : 'Not cloned')}
                          </span>
                        </td>
                        <td>
                          <Button
                            size="small"
                            variant={product.existsInTarget ? 'secondary' : 'primary'}
                            disabled={product.existsInTarget || cloningId === product.id}
                            onClick={() => handleClone(product)}
                          >
                            {product.existsInTarget
                              ? (isRtl ? 'موجود بالفعل' : 'Already exists')
                              : cloningId === product.id
                                ? (isRtl ? 'جاري الاستنساخ...' : 'Cloning...')
                                : (isRtl ? 'استنساخ' : 'Clone')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 ? (
          <div className="clone-pagination">
            <Button
              size="small"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {isRtl ? 'السابق' : 'Previous'}
            </Button>
            <span className="text-muted">
              {isRtl ? `صفحة ${page} من ${totalPages}` : `Page ${page} of ${totalPages}`}
            </span>
            <Button
              size="small"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {isRtl ? 'التالي' : 'Next'}
            </Button>
          </div>
        ) : null}
      </div>

      <style>{`
        .product-cell {
          display: flex;
          align-items: center;
          gap: var(--sp-3);
        }
        .product-thumb-wrap {
          position: relative;
          width: 44px;
          height: 44px;
          flex-shrink: 0;
          border-radius: var(--r-md);
          overflow: hidden;
          background: var(--neutral-100);
        }
        .product-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .product-info {
          min-width: 0;
        }
        .product-name {
          font-weight: var(--fw-semibold);
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .product-name-sub {
          font-size: var(--fs-xs);
          color: var(--text-muted);
        }
        .card-header-info {
          font-size: var(--fs-sm);
        }
        .clone-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--sp-4);
          padding: var(--sp-4);
          border-top: 1px solid var(--border-light);
        }
      `}</style>
    </div>
  );
};

export default CloneProductsIndex;
